import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { MonthlyProgress } from '../types/testResult';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);



interface MonthlyProgressChartProps {
  data: MonthlyProgress[];
  height?: number;
}

/**
 * Monthly Progress Chart Component
 */
export const MonthlyProgressChart: React.FC<MonthlyProgressChartProps> = ({
  data,
  height = 300
}) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const month = data[context.dataIndex];
            return [
              `Average: ${context.parsed.y}%`,
              `Tests taken: ${month.testsCount}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    },
  };

  const formatMonthLabel = (monthString: string) => {
    const [year, month] = monthString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const chartData = {
    labels: data.map(item => formatMonthLabel(item.month)),
    datasets: [
      {
        label: 'Average Performance',
        data: data.map(item => item.averagePercentage),
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500"
        style={{ height: `${height}px` }}
      >
        No monthly progress data available
      </div>
    );
  }

  // Calculate trend
  const calculateTrend = () => {
    if (data.length < 2) return null;

    const recent = data[data.length - 1];
    const previous = data[data.length - 2];
    const change = recent.averagePercentage - previous.averagePercentage;

    return {
      change: change.toFixed(1),
      isPositive: change > 0,
      isNeutral: Math.abs(change) < 0.1
    };
  };

  const trend = calculateTrend();

  return (
    <div>
      <div style={{ height: `${height}px` }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Trend indicator */}
      {trend && (
        <div className="mt-4 flex items-center justify-center">
          <div className={`flex items-center px-3 py-1 rounded-full text-sm ${
            trend.isNeutral
              ? 'bg-gray-100 text-gray-700'
              : trend.isPositive
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            <span className="mr-1 flex items-center">
              {trend.isNeutral ? <ArrowRight className="w-4 h-4" aria-hidden="true" /> : trend.isPositive ? <TrendingUp className="w-4 h-4" aria-hidden="true" /> : <TrendingDown className="w-4 h-4" aria-hidden="true" />}
            </span>
            {trend.isNeutral
              ? 'No change from last month'
              : `${trend.isPositive ? '+' : ''}${trend.change}% from last month`
            }
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
        <div>
          <div className="font-medium text-gray-900">
            {data.reduce((sum, item) => sum + item.testsCount, 0)}
          </div>
          <div className="text-gray-500">Total Tests</div>
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {data.length > 0 ? (data.reduce((sum, item) => sum + item.averagePercentage, 0) / data.length).toFixed(1) : 0}%
          </div>
          <div className="text-gray-500">Overall Average</div>
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {data.length > 0 ? Math.max(...data.map(item => item.averagePercentage)).toFixed(1) : 0}%
          </div>
          <div className="text-gray-500">Best Month</div>
        </div>
      </div>
    </div>
  );
};
